import { UserAgentConfig } from './agents'
import { SystemEmbeddingModelConfig, UserEmbeddingModelConfig } from './embeddings'
import { ModelConfig, ModelOptions, SystemModelConfig, UserModelConfig } from './models'
import { ProviderKey } from './providers'

/**
 * Optional properties specific to certain models, primarily seen in Anthropic/Groq responses.
 */
export interface RosettaModelProperties {
  /** A brief description of the model's capabilities or intended use. */
  description?: string
  /** A summary of the model's strengths or key features. */
  strengths?: string
  /** Indicates if the model has strong multilingual capabilities. */
  multilingual?: boolean
  /** Indicates if the model supports vision (image) input. */
  vision?: boolean
  /** Anthropic specific: Indicates support for extended thinking steps. */
  extended_thinking?: boolean
  /** Anthropic specific: A qualitative measure of the model's latency. */
  comparative_latency?: string
  /** Anthropic specific: Estimated cost per million input tokens. */
  cost_input_mtok?: number
  /** Anthropic specific: Estimated cost per million output tokens. */
  cost_output_mtok?: number
  /** Anthropic specific: The cutoff date for the model's training data. */
  training_data_cutoff?: string
  /** Anthropic specific: Maximum completion tokens potentially available under specific conditions. */
  extended_max_completion_tokens?: number
}
/**
 * Represents a single AI model available through a provider,
 * combining common fields and provider-specific optional details.
 */
export interface RosettaModel {
  /** Unique identifier for the model (provider-specific format). */
  id: string
  /** Object type, always "model". */
  object: 'model'
  /** The organization that owns the model (e.g., "openai", "google", "anthropic", "meta"). */
  owned_by: string
  /** Unix timestamp (seconds) when the model was created, or null if not available. */
  created?: number | null
  /** Indicates if the model is currently active or available for use. */
  active?: boolean
  /** The maximum context window size in tokens. */
  context_window?: number
  /** Indicates availability in public applications (observed as null, kept for potential future use). */
  public_apps?: string | null
  /** The maximum number of tokens that can be generated in a single completion request. */
  max_completion_tokens?: number
  /** Additional, often provider-specific, properties and capabilities of the model. */
  properties?: RosettaModelProperties
  /** The provider this model belongs to. Added by RosettaAI for context. */
  readonly provider: ProviderKey
  /** Store the raw data from the provider for debugging or advanced use */
  readonly rawData?: Record<string, any>
}
/**
 * Represents the standard list response structure for models.
 */
export interface RosettaModelList {
  /** Object type, always "list". */
  object: 'list'
  /** An array of available models. */
  data: RosettaModel[]
}

export interface OpenAICompletionMessage {
  role: string
  content: string
  name?: string
}

export interface AnthropicMessageProviderState {
  rawContentBlocks?: unknown[]
  assistantTurnBoundary?: boolean
}

export interface RosettaMessageProviderState {
  anthropic?: AnthropicMessageProviderState
}

export interface GenerateResultProviderState {
  anthropic?: {
    containerId?: string
    expiresAt?: string
    rawContentBlocks?: unknown[]
    assistantTurnBoundary?: boolean
  }
}

export type MessageRetryFailureKind = 'stream' | 'provider' | 'timeout' | 'audio' | 'unknown'

export interface MessageRetryState {
  failed: boolean
  retryable: boolean
  manualRetryAvailable: boolean
  autoRetryAttempted: boolean
  autoRetryPending?: boolean
  failureKind: MessageRetryFailureKind
  errorMessage?: string
}

export interface ModelMessage {
  role: string
  content: string | MessageContent[] | null
  name?: string
  tool_calls?: Array<{ id: string; type: string; function: { name: string; arguments: string } }>
  tool_call_id?: string
  providerState?: RosettaMessageProviderState
  rawContentBlocks?: unknown[]
  tokenUsage?: TokenUsage
  toolEvents?: ToolEvent[]
  retryState?: MessageRetryState
  /**
   * Terminal finish reason for a completed assistant turn, when the UI needs to render a state for it
   * (currently only refusals — see `isRefusalFinishReason`). UI-only metadata: stripped before the
   * message is replayed to a completion endpoint.
   */
  finishReason?: string | null
}

/**
 * Finish reasons that mean the model declined to produce (or continue) the response rather than
 * completing normally. Both spellings describe the same provider state:
 * - `'content_filter'` — what the api's OpenAI-compatible stream mapper emits; it maps rosetta's
 *   `'refusal'` onto the closest OpenAI finish reason, so this is the value the chat SSE carries.
 * - `'refusal'` — the raw rosetta value, exposed unmapped on non-streaming `GenerateResult`.
 */
const REFUSAL_FINISH_REASONS: readonly string[] = ['refusal', 'content_filter']

/**
 * True when a finish reason represents a refusal / safety-filtered turn. Every other reason
 * (`'stop'`, `'tool_calls'`, `'length'`, `'error'`, `null`, `undefined`, …) returns false.
 */
export function isRefusalFinishReason(finishReason?: string | null): boolean {
  return typeof finishReason === 'string' && REFUSAL_FINISH_REASONS.includes(finishReason)
}

export interface MessageContent {
  type: 'text' | 'image'
  text?: string
  image?: {
    mimeType: string
    base64Data: string
  }
}

// CompletionApiRequest no longer needs url/apiKey
export interface CompletionApiRequest {
  messages: ModelMessage[]
  modelConfig: ModelConfig // Pass the full config to derive model name and options
  onRetry?: () => any
  options?: ModelOptions // Request-specific options override model defaults
  providerState?: GenerateResultProviderState
  timeout?: number;
  requestTimeoutMs?: number
  maxRetries?: number
}

export interface StreamingCompletionApiRequest {
  messages: ModelMessage[]
  modelConfig: ModelConfig
  sessionId?: string
  onChunk: (chunk: string) => void
  /** `finishReason` is the terminal finish reason observed on the stream, or null if none was sent. */
  onComplete: (finalContent: string, usage?: TokenUsage, finishReason?: string | null) => void
  onError: (error: Error) => void
  onCancel?: () => void
  onMeta?: (meta: { runId?: string; contextMessages?: any[]; providerState?: GenerateResultProviderState }) => void
  onRetry?: () => any
  options?: ModelOptions
  providerState?: GenerateResultProviderState
  signal?: AbortSignal
  timeout?: number
  requestTimeoutMs?: number
  maxRetries?: number
}

export interface StreamingAgentCompletionApiRequest {
  messages: ModelMessage[]
  name: string
  sessionId?: string
  onChunk: (chunk: string) => void
  /** `finishReason` is the terminal finish reason observed on the stream, or null if none was sent. */
  onComplete: (finalContent: string, usage?: TokenUsage, finishReason?: string | null) => void
  onError: (error: Error) => void
  onCancel?: () => void
  onMeta?: (meta: { runId?: string; contextMessages?: any[]; providerState?: GenerateResultProviderState }) => void
  providerState?: GenerateResultProviderState
  signal?: AbortSignal
  timeout?: number
  requestTimeoutMs?: number
  maxRetries?: number
}

export interface GeneratePromptRequest {
  prompt: string
  modelConfig: ModelConfig // Pass the full config to derive model name and options
  options?: ModelOptions
}

/**
 * Represents citation metadata, often related to grounding.
 * @property text - The text content of the citation (may not always be provided).
 * @property sourceId - An identifier linking to the source material (e.g., URI, document ID).
 * @property startIndex - The starting index in the main response content where the citation applies (optional).
 * @property endIndex - The ending index in the main response content where the citation applies (optional).
 */
export interface Citation {
  text?: string
  sourceId: string
  startIndex?: number
  endIndex?: number
}

/**
 * Represents a tool call requested by the model in its response.
 * Contains the raw arguments string as received from the provider.
 *
 * @property id - A unique identifier for this specific tool call instance.
 * @property type - The type of tool called (currently 'function').
 * @property function - Details of the function call.
 * @property function.name - The name of the function the model wants to call.
 * @property function.arguments - A JSON string containing the arguments the model generated for the function call.
 */
export interface RosettaToolCallRequest {
  id: string
  type: 'function'
  function: {
    name: string
    arguments: string // Raw JSON string
  }
}

/**
 * Represents token usage statistics for an API call.
 * Fields are optional as not all providers/responses include all details.
 */
export interface TokenUsage {
  promptTokens?: number
  completionTokens?: number
  totalTokens?: number
  /** Tokens related to cached content (Google specific). */
  cachedContentTokenCount?: number
}

/**
 * Represents a tool event that occurred during message processing.
 * This is UI-only metadata that should not be sent to APIs.
 */
export interface ToolEvent {
  type: string
  message?: string
  data?: any
  sessionId?: string
  clientId?: string
  timestamp?: number
}

/**
 * The result structure for a non-streaming generation request (`RosettaAI.generate`).
 */
export interface GenerateResult {
  /** The primary text content of the response, or null if none was generated (e.g., only tool calls). */
  content: string | null
  /** Tool calls requested by the model, if any. */
  toolCalls?: RosettaToolCallRequest[]
  /** Reason the generation finished (e.g., 'stop', 'length', 'tool_calls', 'content_filter', 'error', 'recitation_filter'). */
  finishReason: string | null
  /** Token usage statistics, if provided by the API. */
  usage?: TokenUsage
  /** Citations or grounding information, if provided by the API (e.g., Google grounding). */
  citations?: Citation[]
  /** Intermediate thinking steps, if requested and provided (Anthropic specific). */
  thinkingSteps?: string | null
  /** The parsed JSON object if `responseFormat: { type: 'json_object' }` was requested and parsing succeeded. Null otherwise. */
  parsedContent?: Record<string, unknown> | Array<unknown> | null
  /** The exact model ID string used for the completion, as reported by the provider. */
  model: string
  /** The raw response object from the underlying SDK (use with caution, structure varies). */
  rawResponse?: unknown
}

export type MergedModel =
  | SystemModelConfig
  | UserModelConfig
  | UserAgentConfig
  | SystemEmbeddingModelConfig
  | UserEmbeddingModelConfig

export interface MergedModelMap {
  [key: string]: MergedModel
}

export function isSystemModelConfig(config?: MergedModel): config is SystemModelConfig {
  return config != null && config.type === 'system-model'
}

export function isUserModelConfig(config?: MergedModel): config is UserModelConfig {
  return config != null && config.type === 'user-model'
}

export type ProviderLLM = {
  object: 'model'
  id: string
  provider: string
}

export type ProviderVoice = {
  object: 'voice'
  id: string
  provider: string
  name?: string
  category?: string
  labels?: Record<string, string>
  previewUrl?: string
  owned?: boolean
}

/**
 * Unified provider inventory item returned by POST /v1/core/models.
 * Discriminated by the 'object' field: 'model' | 'voice'.
 * - For voices, display 'name' to users; 'id' is provider's opaque voice id.
 */
export type ProviderInventoryItem = ProviderLLM | ProviderVoice

/**
 * Unified provider inventory list shape for discovery.
 */
export interface ProviderInventoryList {
  object: 'list'
  data: ProviderInventoryItem[]
}
